import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
    Row,
    Col,
    Card,
    Table,
    Button,
    Space,
    Tag,
    Tooltip,
    Modal,
    Form,
    Input,
    Select,
    message,
    Popconfirm,
    Drawer,
    Statistic,
    Progress,
    Typography
} from 'antd';
import { Animated } from "react-animated-css";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    GlobalOutlined,
    MobileOutlined,
    DesktopOutlined,
    TabletOutlined,
    PlayCircleOutlined,
    PictureOutlined,
    AppstoreOutlined,
    DollarOutlined,
    BarChartOutlined
} from '@ant-design/icons';

import './AdUnitManagement.scss';
import AdUnitDetail from './components/AdUnitDetail';
import AdUnitForm from './components/AdUnitForm';
import { getAdFormatLabel, normalizeAdFormatFamily } from './adFormatUtils';

// modules
import { adunitList, adunitCreate, adunitUpdate, adunitModeration, adunitDelete } from '../../modules/adunit';

const { Option } = Select;
const { Text, Title } = Typography;

const AD_PLAN_OPTIONS = ['no_plan', 'advs', 'no_advs', 'avod'];

const normalizeAdPlans = (value) => {
    if (!value) {
        return [];
    }
    const arrayValue = Array.isArray(value) ? value : [value];
    return Array.from(
        new Set(
            arrayValue
                .filter(Boolean)
                .filter(plan => AD_PLAN_OPTIONS.includes(plan))
        )
    );
};

class AdUnitManagement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            adUnits: [],
            filteredAdUnits: [],
            selectedAdUnit: null,
            showCreateModal: false,
            showDetailDrawer: false,
            editingAdUnit: null,
            filters: {
                platform: 'all',
                adFormat: 'all',
                adType: 'all',
                status: 'all',
                businessUnit: 'all',
                adPlacement: 'all',
                adPlans: []
            },
            searchText: '',
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0
            }
        };
    }

    componentDidMount() {
        this.loadAdUnits();
    }

    // Mock API call - replace with actual API
    loadAdUnits = async () => {
        this.setState({ loading: true });
        const isSuperAdmin = this.isSuperAdmin();

        const listPayload = isSuperAdmin ? { showMore: true, codeNaming: false } : { showMore: false, codeNaming: true };
        listPayload.status = 'all';

        this.props.adunitList(listPayload).then((res) => {
            const results = isSuperAdmin ? res.results : res.results.filter(r => r.status !== 'deleted');
            const normalizedResults = results.map(unit => ({
                ...unit,
                adPlans: normalizeAdPlans(unit.adPlans),
                adPlacement: unit.adPlacement || '',
            }));
            this.setState({
                adUnits: normalizedResults,
                loading: false,
                pagination: {
                    ...this.state.pagination,
                    total: normalizedResults.length,
                }
            }, this.applyFilters);
        })
    };

    isSuperAdmin = () => {
        const { userProfile } = this.props;
        return userProfile?.user?.role === 'superadmin';
    };

    handleSearch = (value) => {
        this.setState({ searchText: value }, this.applyFilters);
    };

    handleFilterChange = (filterType, value) => {
        const normalizedValue = filterType === 'adPlans'
            ? (Array.isArray(value) ? value : [])
            : value;

        this.setState({
            filters: {
                ...this.state.filters,
                [filterType]: normalizedValue
            }
        }, this.applyFilters);
    };

    applyFilters = () => {
        const { adUnits, filters, searchText } = this.state;
        let filtered = adUnits;

        // Apply search
        if (searchText) {
            filtered = filtered.filter(unit => {
                const loweredSearch = searchText.toLowerCase();
                const matchesName = typeof unit.name === 'string' &&
                    unit.name.toLowerCase().includes(loweredSearch);
                const matchesAssetId = typeof unit.assetId === 'string' &&
                    unit.assetId.toLowerCase().includes(loweredSearch);
                return matchesName || matchesAssetId;
            });
        }

        // Apply filters
        Object.keys(filters).forEach(key => {
            const filterValue = filters[key];
            if (key === 'adPlans') {
                if (Array.isArray(filterValue) && filterValue.length > 0) {
                    filtered = filtered.filter(unit => {
                        const unitPlans = normalizeAdPlans(unit.adPlans);
                        return filterValue.some(plan => unitPlans.includes(plan));
                    });
                }
                return;
            }

            if (filterValue !== 'all') {
                if (key === 'adFormat' && filterValue === 'interstitial') {
                    filtered = filtered.filter(unit =>
                        ['interstitial', 'interstitial_backup'].includes(unit.adFormat)
                    );
                } else if (key === 'adFormat') {
                    filtered = filtered.filter(unit => normalizeAdFormatFamily(unit.adFormat) === filterValue);
                } else {
                    filtered = filtered.filter(unit => unit[key] === filterValue);
                }
            }
        });

        this.setState({
            filteredAdUnits: filtered,
            pagination: {
                ...this.state.pagination,
                current: 1,
                total: filtered.length
            }
        });
    };

    handleTableChange = (pagination, filters, sorter) => {
        this.setState({
            pagination: {
                ...this.state.pagination,
                current: pagination.current,
                pageSize: pagination.pageSize
            }
        });
    };

    handleCreateAdUnit = () => {
        this.setState({
            showCreateModal: true,
            editingAdUnit: null
        });
    };

    handleEditAdUnit = (adUnit) => {
        this.setState({
            showCreateModal: true,
            editingAdUnit: adUnit
        });
    };

    handleDeleteAdUnit = (assetId) => {
        this.props.adunitDelete(assetId)
            .then(() => {
                message.success('Ad Unit deleted successfully');
                this.loadAdUnits();
            })
            .catch(error => {
                message.error('Failed to delete Ad Unit');
            });
    };

    handleViewDetail = (adUnit) => {
        this.setState({
            selectedAdUnit: adUnit,
            showDetailDrawer: true
        });
    };

    handleModalClose = () => {
        this.setState({
            showCreateModal: false,
            editingAdUnit: null
        });
    };

    handleDrawerClose = () => {
        this.setState({
            showDetailDrawer: false,
            selectedAdUnit: null
        });
    };

    handleFormSubmit = (values) => {
        const { editingAdUnit } = this.state;
        const isSuperAdmin = this.isSuperAdmin();
        const sanitizedValues = { ...values };
        const protectedFields = [
            'adPartnerUnitId',
            'adPartnerAppId',
            'adPartnerUnitPath',
            'adPartner',
            'assetId',
            'asset_id'
        ];

        if (!isSuperAdmin) {
            protectedFields.forEach(field => {
                if (editingAdUnit && Object.prototype.hasOwnProperty.call(editingAdUnit, field)) {
                    sanitizedValues[field] = editingAdUnit[field];
                } else {
                    delete sanitizedValues[field];
                }
            });
        }

        if (sanitizedValues.assetId && !sanitizedValues.asset_id) {
            sanitizedValues.asset_id = sanitizedValues.assetId;
        }

        sanitizedValues.adPlans = normalizeAdPlans(sanitizedValues.adPlans);

        if (editingAdUnit) {
            // Update existing ad unit
            const updatedAdUnit = {
                ...editingAdUnit,
                ...sanitizedValues,
            };

            // add action
            if (updatedAdUnit.status === 'revoked') {
                updatedAdUnit.action = 'toRevoke';
            } else if (updatedAdUnit.status === 'deleted') {
                updatedAdUnit.action = 'toDelete';
            } else if (!isSuperAdmin) {
                updatedAdUnit.action = 'toSubmit';
            }

            if (isSuperAdmin) {
                this.props.adunitModeration(updatedAdUnit.assetId, updatedAdUnit).then(res => {
                    this.loadAdUnits();
                })
            } else {
                this.props.adunitUpdate(updatedAdUnit.assetId, updatedAdUnit).then(res => {
                    this.loadAdUnits();
                })
            }
            message.success('Ad Unit updated successfully');
        } else {
            // Create new ad unit
            const newAdUnit = {
                ...sanitizedValues,
            };

            const creationStatus = isSuperAdmin && sanitizedValues.status
                ? sanitizedValues.status
                : 'submitted';

            if (creationStatus) {
                newAdUnit.status = creationStatus;
            }

            // remove the undefined value from newAdUnit
            Object.keys(newAdUnit).forEach(key => newAdUnit[key] === undefined && delete newAdUnit[key]);
            this.props.adunitCreate(newAdUnit).then(() => {
                this.loadAdUnits();
            });
            message.success('Ad Unit created successfully');
        }

        this.handleModalClose();
    };

    getPlatformIcon = (platform) => {
        const icons = {
            web: <GlobalOutlined />,
            ios: <MobileOutlined />,
            android: <MobileOutlined />,
            huawei: <TabletOutlined />
        };
        return icons[platform] || <DesktopOutlined />;
    };

    getFormatIcon = (format) => {
        const icons = {
            banner: <PictureOutlined />,
            interstitial: <AppstoreOutlined />,
            instream_video: <PlayCircleOutlined />,
            web_native: <GlobalOutlined />,
            app_native: <MobileOutlined />
        };
        return icons[normalizeAdFormatFamily(format)] || <PictureOutlined />;
    };

    getStatusColor = (status) => {
        const colors = {
            active: 'success',
            revoked: 'default',
            submitted: 'processing',
            rejected: 'error'
        };
        return colors[status] || 'default';
    };

    getAdPlanColor = (plan) => {
        const colors = {
            no_plan: 'default',
            advs: 'green',
            no_advs: 'orange',
        };
        return colors[plan] || 'default';
    };

    renderSummaryCards = () => {
        const { filteredAdUnits } = this.state;

        const summary = filteredAdUnits.reduce((acc, unit) => {
            acc.total++;
            acc[unit.status] = (acc[unit.status] || 0) + 1;
            acc.totalRevenue += unit.revenue || 0;
            acc.totalImpressions += unit.impressions || 0;
            return acc;
        }, { total: 0, totalRevenue: 0, totalImpressions: 0 });

        return (
            <Row gutter={[16, 16]} className="summary-cards">
                <Col xs={12} sm={6} lg={6}>
                    <Card className="summary-card">
                        <Statistic
                            title="Total Ad Units"
                            value={summary.total}
                            prefix={<AppstoreOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6} lg={6}>
                    <Card className="summary-card">
                        <Statistic
                            title="Active Units"
                            value={summary.active || 0}
                            valueStyle={{ color: '#3f8600' }}
                            prefix={<BarChartOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6} lg={6}>
                    <Card className="summary-card">
                        <Statistic
                            title="Total Revenue"
                            value={summary.totalRevenue}
                            precision={2}
                            prefix={<DollarOutlined />}
                            suffix="USD"
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6} lg={6}>
                    <Card className="summary-card">
                        <Statistic
                            title="Total Impressions"
                            value={summary.totalImpressions}
                            prefix={<EyeOutlined />}
                        />
                    </Card>
                </Col>
            </Row>
        );
    };

    renderFilters = () => {
        const { filters } = this.state;
        const { adUnits } = this.state;

        const businessUnits = [...new Set(adUnits.map(unit => unit.businessUnit))];
        const placements = [...new Set(adUnits.map(unit => unit.adPlacement).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b));
        const planOptions = AD_PLAN_OPTIONS;

        return (
            <Card className="filter-card">
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={6}>
                        <Input.Search
                            placeholder="Search ad units..."
                            allowClear
                            onSearch={this.handleSearch}
                            onChange={(e) => this.handleSearch(e.target.value)}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                        <Select
                            placeholder="Platform"
                            value={filters.platform}
                            onChange={(value) => this.handleFilterChange('platform', value)}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">All Platforms</Option>
                            <Option value="web">Web</Option>
                            <Option value="ios">iOS</Option>
                            <Option value="android">Android</Option>
                            <Option value="huawei">Huawei</Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                        <Select
                            placeholder="Ad Format"
                            value={filters.adFormat}
                            onChange={(value) => this.handleFilterChange('adFormat', value)}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">All Formats</Option>
                            <Option value="banner">Banner</Option>
                            <Option value="interstitial">Interstitial</Option>
                            <Option value="instream_video">Video</Option>
                            <Option value="web_native">Web Native</Option>
                            <Option value="app_native">App Native</Option>
                            <Option value="native_3adbanner">native_3adbanner</Option>
                            <Option value="app_open">App Open</Option>
                            <Option value="rewarded">Rewarded</Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                        <Select
                            placeholder="Status"
                            value={filters.status}
                            onChange={(value) => this.handleFilterChange('status', value)}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">All Status</Option>
                            <Option value="active">Active</Option>
                            <Option value="revoked">Revoked</Option>
                            <Option value="submitted">Submitted</Option>
                            <Option value="rejected">Rejected</Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                        <Select
                            mode="tags"
                            placeholder="Ad Plans"
                            value={filters.adPlans}
                            onChange={(values) => this.handleFilterChange('adPlans', values)}
                            style={{ width: '100%' }}
                            allowClear
                        >
                            {planOptions.map(plan => (
                                <Option key={plan} value={plan}>{plan.replace(/_/g, ' ')}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                        <Select
                            placeholder="Business Unit"
                            value={filters.businessUnit}
                            onChange={(value) => this.handleFilterChange('businessUnit', value)}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">All Units</Option>
                            {businessUnits.map(unit => (
                                <Option key={unit} value={unit}>{unit}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                        <Select
                            placeholder="Placement"
                            value={filters.adPlacement}
                            onChange={(value) => this.handleFilterChange('adPlacement', value)}
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp="children"
                        >
                            <Option value="all">All Placements</Option>
                            {placements.map(placement => (
                                <Option key={placement} value={placement}>{placement}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={2}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={this.handleCreateAdUnit}
                            style={{ width: '100%' }}
                        >
                            Create
                        </Button>
                    </Col>
                </Row>
            </Card>
        );
    };

    renderTable = () => {
        const { filteredAdUnits, loading, pagination } = this.state;
        const isSuperAdmin = this.isSuperAdmin();

        const columns = [
            {
                title: 'Asset ID',
                dataIndex: 'assetId',
                key: 'assetId',
                width: 110,
                className: 'sticky-col sticky-col-left',
                onHeaderCell: () => ({ className: 'sticky-col sticky-col-left' }),
                render: (text) => (
                    <Text code strong>{text}</Text>
                )
            },
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
                width: 200,
                ellipsis: true,
                render: (text, record) => (
                    <div className="name-cell">
                        <div className="name-text"><Tooltip title={text}>{text}</Tooltip></div>
                        <div className="name-meta">
                            <Tag size="small">{record.businessUnit}</Tag>
                        </div>
                    </div>
                )
            },
            {
                title: 'Platform',
                dataIndex: 'platform',
                key: 'platform',
                width: 100,
                render: (platform) => (
                    <div className="platform-cell">
                        {this.getPlatformIcon(platform)}
                        <span>{platform}</span>
                    </div>
                )
            },
            {
                title: 'Format',
                dataIndex: 'adFormat',
                key: 'adFormat',
                width: 120,
                render: (format) => (
                    <div className="format-cell">
                        {this.getFormatIcon(format)}
                        <span>{getAdFormatLabel(format)}</span>
                    </div>
                )
            },
            {
                title: 'Placement',
                dataIndex: 'adPlacement',
                key: 'adPlacement',
                width: 130,
                ellipsis: true,
                render: (placement) => placement ? (
                    <Tooltip title={placement}>
                        <Text>{placement}</Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">N/A</Text>
                )
            },
            {
                title: 'Plans',
                dataIndex: 'adPlans',
                key: 'adPlans',
                width: 160,
                render: (plans = []) => {
                    const normalizedPlans = normalizeAdPlans(plans);
                    if (!normalizedPlans.length) {
                        return <Text type="secondary">None</Text>;
                    }
                    return (
                        <Space size={[4, 4]} wrap>
                            {normalizedPlans.map(plan => (
                                <Tag key={plan} color={this.getAdPlanColor(plan)}>
                                    {plan.replace(/_/g, ' ')}
                                </Tag>
                            ))}
                        </Space>
                    );
                }
            },
            {
                title: 'Type',
                dataIndex: 'adType',
                key: 'adType',
                width: 80,
                render: (type) => (
                    <Tag color={type === 'mature' ? 'red' : 'blue'}>
                        {type}
                    </Tag>
                )
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 110,
                render: (status) => (
                    <Tag color={this.getStatusColor(status)}>
                        {status.toUpperCase()}
                    </Tag>
                )
            },
            {
                title: 'Revenue',
                dataIndex: 'revenue',
                key: 'revenue',
                width: 100,
                align: 'right',
                render: (revenue) => (
                    <Text strong>${(revenue || 0).toFixed(2)}</Text>
                )
            },
            {
                title: 'Impressions',
                dataIndex: 'impressions',
                key: 'impressions',
                width: 100,
                align: 'right',
                render: (impressions) => (
                    <Text>{(impressions || 0).toLocaleString()}</Text>
                )
            },
            {
                title: 'CTR',
                dataIndex: 'ctr',
                key: 'ctr',
                width: 80,
                align: 'right',
                render: (ctr) => (
                    <Text>{(ctr || 0).toFixed(2)}%</Text>
                )
            },
            {
                title: 'Actions',
                key: 'actions',
                width: 150,
                className: 'sticky-col sticky-col-right',
                onHeaderCell: () => ({ className: 'sticky-col sticky-col-right' }),
                render: (_, record) => (
                    <Space size="small">
                        <Tooltip title="View Details">
                            <Button
                                type="text"
                                icon={<EyeOutlined />}
                                onClick={() => this.handleViewDetail(record)}
                            />
                        </Tooltip>
                        <Tooltip title="Edit">
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => this.handleEditAdUnit(record)}
                            />
                        </Tooltip>
                        {
                            isSuperAdmin &&
                            <Popconfirm
                                title={<span>Are you sure you want to delete this ad unit?<br />This action cannot be reverted.</span>}
                                onConfirm={() => this.handleDeleteAdUnit(record.assetId)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Tooltip title="Delete">
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                    />
                                </Tooltip>
                            </Popconfirm>
                        }
                    </Space >
                )
            }
        ];

        const totalColumnWidth = columns.reduce((sum, column) => (
            typeof column.width === 'number' ? sum + column.width : sum
        ), 0);

        return (
            <Card className="table-card">
                <Table
                    className="adunit-table"
                    columns={columns}
                    dataSource={filteredAdUnits}
                    rowKey="assetId"
                    loading={loading}
                    tableLayout="fixed"
                    style={totalColumnWidth ? { '--adunit-table-min-width': `${totalColumnWidth}px` } : undefined}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    onChange={this.handleTableChange}
                />
            </Card>
        );
    };

    render() {
        const {
            showCreateModal,
            showDetailDrawer,
            selectedAdUnit,
            editingAdUnit
        } = this.state;
        const isSuperAdmin = this.isSuperAdmin();

        return (
            <Animated animationIn="fadeIn">
                <div className="adunit-management-wrapper">
                    <div className="page-header">
                        <Title level={2}>Ad Unit Management</Title>
                        <Text type="secondary">
                            Manage your advertising units and monitor their performance
                        </Text>
                    </div>

                    {/* Summary Cards */}
                    {this.renderSummaryCards()}

                    {/* Filters */}
                    {this.renderFilters()}

                    {/* Table */}
                    {this.renderTable()}

                    {/* Create/Edit Modal */}
                    <Modal
                        title={editingAdUnit ? 'Edit Ad Unit' : 'Create New Ad Unit'}
                        open={showCreateModal}
                        onCancel={this.handleModalClose}
                        footer={null}
                        width={800}
                        destroyOnClose
                    >
                        <AdUnitForm
                            initialValues={editingAdUnit}
                            isSuperAdmin={isSuperAdmin}
                            onSubmit={this.handleFormSubmit}
                            onCancel={this.handleModalClose}
                        />
                    </Modal>

                    {/* Detail Drawer */}
                    <Drawer
                        title="Ad Unit Details"
                        placement="right"
                        onClose={this.handleDrawerClose}
                        open={showDetailDrawer}
                        width={1000}
                        destroyOnClose
                    >
                        {selectedAdUnit && (
                            <AdUnitDetail
                                adUnit={selectedAdUnit}
                                isSuperAdmin={isSuperAdmin}
                                onEdit={() => {
                                    this.handleDrawerClose();
                                    this.handleEditAdUnit(selectedAdUnit);
                                }}
                            />
                        )}
                    </Drawer>
                </div>
            </Animated>
        );
    }
}

const mapStateToProps = state => ({
    router: state.router,
    userProfile: state.user.userProfile,
    selectedOrganization: state.user.selectedOrganization,
    env: state.user.env,
});

const mapDispatchToProps = dispatch => bindActionCreators({
    adunitList,
    adunitCreate,
    adunitUpdate,
    adunitModeration,
    adunitDelete,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AdUnitManagement);
